import { ActionModal, FormError, FormLayout, PageLayout } from '@kcftech/design-system';
import {
    AlertThresholdRequest,
    AlertThresholdRequestMetricTypeConfig,
    ISystemOverviewResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useRunningAlertThresholdJobs } from 'custom_hooks/data_hooks/jobs/useRunningAlertThresholdJobs';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { RequiredMessage } from 'shared/form/RequiredMessage';
import { extractErrorString } from 'utils/api';
import { getSystemWorkbenchTab, WorkbenchTab } from 'utils/RouteHelper';
import { sentryCaptureException } from 'utils/sentry';
import { AlarmCard } from './AlarmCard';
import { BoundsCard } from './BoundsCard';
import { LocationSelectionField } from './LocationSelectionField';
import { NotificationCard } from './NotificationCard';
import { RunningThresholdJobsWarningCard } from './RunningThresholdJobsWarningCard';
import { SamplingCard } from './SamplingCard';
import { AlarmThresholdFormFields, INDICATOR_CHART_ITEMS } from './shared';
import {
    AlarmAndNotificationWrapper,
    FlushPrimary,
    StyledCheckbox,
    StyledLink,
    StyledSecondary,
} from './styled';

function toNumberOrUndefined(value: number | string | undefined) {
    if (value === undefined || value === '') {
        return undefined;
    }

    return Number(value);
}

export type GenerateAlarmThresholdsModalProps = Readonly<{
    locationId: string;
    system?: ISystemOverviewResponse;
    onClose: () => void;
}>;

export function GenerateAlarmThresholdsModal({
    locationId,
    system,
    onClose,
}: GenerateAlarmThresholdsModalProps) {
    const { showToast } = useNotificationContext();

    const { runningThresholdJobs, runningThresholdJobsError } =
        useRunningAlertThresholdJobs(locationId);

    const toAlertThresholdRequest = (data: AlarmThresholdFormFields) => {
        const selectedIndicatorTypes = data.indicatorTypes
            .filter(({ selected }) => selected)
            .map(
                ({ metricType, minAlarmBound, maxAlarmBound }) =>
                    new AlertThresholdRequestMetricTypeConfig({
                        MetricType: metricType,
                        MinAlarmBound: toNumberOrUndefined(minAlarmBound),
                        MaxAlarmBound: toNumberOrUndefined(maxAlarmBound),
                    }),
            );

        return new AlertThresholdRequest({
            LocationId: locationId,
            OverwriteExistingThresholds: !data.onlyToNotAlreadySetIndicators,
            GroupIds: data.groupIds,
            IndicatorTypes: selectedIndicatorTypes,
            StartDate: data.start ?? undefined,
            EndDate: data.end ?? undefined,
            WarningPercentage: toNumberOrUndefined(data.warning),
            CriticalPercentage: toNumberOrUndefined(data.criticalAlarm),
            NotificationCriteriaPercentage: Number(data.percentage),
            NotificationCriteriaPoints: Number(data.points),
        });
    };

    const methods = useForm<AlarmThresholdFormFields>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        defaultValues: {
            start: null,
            end: null,
            warning: 70,
            criticalAlarm: 150,
            onlyToNotAlreadySetIndicators: false,
            groupIds: [],
            indicatorTypes: INDICATOR_CHART_ITEMS.map((item) => ({
                metricType: item.metricType,
                selected: false,
                minAlarmBound: item.defaultMin,
                maxAlarmBound: item.defaultMax,
            })),
        },
    });

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = methods;

    const { mutate: generateAlarms, isPending: isGeneratingAlarms } = useMutation({
        mutationFn: async (data: AlarmThresholdFormFields) =>
            sdClient.runAlertThresholdJob({
                body: toAlertThresholdRequest(data),
            }),
        onSuccess: () => {
            showToast({
                type: 'success',
                content: alarmSettingsRoute ? (
                    <>
                        <div className="mb-5">
                            Alarm thresholds generating successfully
                        </div>
                        <StyledLink to={alarmSettingsRoute}>
                            See Alarm Settings
                        </StyledLink>
                    </>
                ) : (
                    'Alarm thresholds generating successfully'
                ),
            });
            onClose();
        },
        onError: (error) => {
            sentryCaptureException({
                error,
                tags: { component: 'GenerateAlarmThresholdsModal' },
                context: { mutationError: { locationId } },
            });

            const errorMessage = extractErrorString(error) ?? 'Unknown error occurred';
            showToast({
                type: 'error',
                content: `Failed to generate alarm thresholds: ${errorMessage}`,
            });
        },
    });

    const alarmSettingsRoute = system
        ? `${getSystemWorkbenchTab(system.Id, WorkbenchTab.Indicators)}/alarmSettings`
        : undefined;

    const onSave = (data: AlarmThresholdFormFields) => {
        generateAlarms(data);
    };

    return (
        <ActionModal
            title="Generate Alarm Thresholds"
            width={1172}
            height={842}
            actionButtonText="Save"
            closeButtonText="Close"
            actionable={!isGeneratingAlarms}
            onAction={handleSubmit(onSave)}
            onClose={() => onClose()}
        >
            <PageLayout layout="task">
                <FormProvider {...methods}>
                    <FlushPrimary className="pb-50">
                        <FormLayout>
                            <span className="mb-10">
                                Automatically generate alarm thresholds using historical
                                sensor data by configuring these fields to establish the
                                baseline.
                            </span>
                            <RunningThresholdJobsWarningCard
                                runningThresholdJobs={runningThresholdJobs}
                                runningThresholdJobsError={runningThresholdJobsError}
                            />
                            <RequiredMessage />
                            <SamplingCard />
                            <BoundsCard />
                            <AlarmAndNotificationWrapper>
                                <AlarmCard />
                                <NotificationCard />
                            </AlarmAndNotificationWrapper>
                            <Controller
                                name="onlyToNotAlreadySetIndicators"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <StyledCheckbox
                                        checked={value}
                                        onChange={onChange}
                                        label="Only apply to indicators without thresholds"
                                    />
                                )}
                            />
                        </FormLayout>
                    </FlushPrimary>
                    <StyledSecondary>
                        {errors.groupIds?.message && (
                            <FormError message={errors.groupIds.message} />
                        )}
                        <LocationSelectionField
                            hideIndicatorGroups
                            rootGroupIds={system ? [system.Id] : undefined}
                        />
                    </StyledSecondary>
                </FormProvider>
            </PageLayout>
        </ActionModal>
    );
}
